import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import { StylesDocuments } from "../StylesDocumentsPdf";
import { ConseilsContext } from "../../Data/ConseilsContext";
import { DateToString } from "../../functions/FonctionsDateHeure";

import ImageMatin from "../Images/Matin.png";
import ImageMidi from "../Images/Midi.png";
import ImageSoir from "../Images/Soir.png";
import ImageCouche from "../Images/Couche.png";
import ImageCheckbox from "../Images/Checkbox.png";
import ImageCheckboxChecked from "../Images/CheckboxChecked.png";

import DocumentPdf from "../DocumentPdf";
import LoaderGlobal from "../../Design/Progression/LoaderGlobal";
import PopupMessageErreur from "../../Design/Messages/PopupMessageErreur";

export default function DocumentConseil({
  conseil_id,
  open,
  onClose,
  autoPrint,
  afficherConseilPrise = true,
}) {
  const Styles = useMemo(() => StyleSheet.create(StylesDocuments), []);
  const { getConseilById } = useContext(ConseilsContext);

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [messageErreur, setMessageErreur] = useState();
  const [error, setError] = useState();

  const handleClose = () => {
    setData(undefined);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    setMessageErreur(undefined);
    setError(undefined);

    getConseilById(conseil_id)
      .then((reponse) => {
        setIsLoading(false);
        if (reponse?.code === "OK") setData(reponse.data);
        else setMessageErreur("Erreur lors de l'impression du conseil");
      })
      .catch((err) => {
        setIsLoading(false);
        setMessageErreur("Erreur lors de l'impression du conseil");
        setError(err);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conseil_id, open]);

  if (messageErreur && open) {
    return (
      <PopupMessageErreur
        messageErreur={messageErreur}
        error={error}
        onClose={handleClose}
      />
    );
  }

  if (!data) return <LoaderGlobal isLoading={isLoading} />;

  const TableHeader = () => (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        height: 24,
        borderTop: "1px solid #D8D8D8",
        borderBottom: "1px solid #D8D8D8",
        paddingVertical: 4,
        marginTop: 10,
      }}
    >
      <Text style={[Styles.titreColonneLeft, { width: 330 }]}>&nbsp;</Text>
      <Text style={[Styles.titreColonne, { width: 40 }]}>Matin</Text>
      <Text style={[Styles.titreColonne, { width: 40 }]}>Midi</Text>
      <Text style={[Styles.titreColonne, { width: 40 }]}>Soir</Text>
      <Text style={[Styles.titreColonne, { width: 40 }]}>Couché</Text>
    </View>
  );

  const moments = [
    { key: "matin", img: ImageMatin },
    { key: "midi", img: ImageMidi },
    { key: "soir", img: ImageSoir },
    { key: "coucher", img: ImageCouche },
  ];

  return (
    <DocumentPdf openDocumentPdf={open} onClose={handleClose} autoPrint={autoPrint}>
      <Document>
        <Page size="A4" style={Styles.page} wrap>
          {/* ===== ENTÊTE ===== */}
          <View style={{ flexDirection: "row", marginBottom: 12 }}>
            {/* Bloc cabinet */}
            <View style={{ flex: 1, paddingRight: 16 }}>
              <Text style={Styles.titreBloc}>{data?.cabinet?.nom}</Text>

              {data?.cabinet?.adresse?.rue ? (
                <Text>{data.cabinet.adresse.rue}</Text>
              ) : null}

              {(data?.cabinet?.adresse?.code_postal || data?.cabinet?.adresse?.ville) ? (
                <Text>
                  {(data?.cabinet?.adresse?.code_postal || "") +
                    " " +
                    (data?.cabinet?.adresse?.ville || "")}
                </Text>
              ) : null}

              {data?.cabinet?.telephone?.numero ? (
                <Text>Tél : {data.cabinet.telephone.numero}</Text>
              ) : null}

              {data?.cabinet?.fax?.numero ? (
                <Text>Fax : {data.cabinet.fax.numero}</Text>
              ) : null}

              {data?.cabinet?.email ? <Text>{data.cabinet.email}</Text> : null}

              {data?.cabinet?.texte_horaire ? <Text>{data.cabinet.texte_horaire}</Text> : null}
              {data?.cabinet?.texte_rdv ? <Text>{data.cabinet.texte_rdv}</Text> : null}

              {data?.cabinet?.texte_libre ? (
                <Text
                  style={[
                    Styles.informationPrincipale,
                    {
                      borderTop: "1px solid #D8D8D8",
                      marginTop: 10,
                      paddingTop: 10,
                      textAlign: "center",
                    },
                  ]}
                >
                  {data.cabinet.texte_libre}
                </Text>
              ) : null}
            </View>

            {/* Bloc titre/date/consultant */}
            <View
              style={{
                flex: 1,
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <View style={{ alignItems: "center", minHeight: 60 }}>
                <Text style={Styles.titre}>CONSEILS</Text>
                <Text>
                  {DateToString(data?.date, {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              </View>

              {data?.consultant ? (
                <View
                  style={{
                    backgroundColor: "#EEEEEE",
                    borderRadius: 8,
                    height: 40,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 200,
                    paddingHorizontal: 12,
                  }}
                >
                  <Text style={Styles.titreBloc}>{data.consultant}</Text>
                </View>
              ) : null}
            </View>
          </View>

          {data?.avertissement_conseil ? (
            <Text
              style={{
                backgroundColor: "#205A71",
                color: "white",
                padding: 10,
                marginTop: 6,
                textAlign: "center",
              }}
            >
              {data.avertissement_conseil}
            </Text>
          ) : null}

          {/* ===== TABLEAU POSOLOGIE ===== */}
          {data?.lignes_conseils?.length > 0 ? <TableHeader /> : null}

          {data?.lignes_conseils?.map((ligne, index) => (
            <View
              key={index}
              wrap={false}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px solid #D8D8D8",
                paddingVertical: 6,
                minHeight: 50,
              }}
            >
              {/* Colonne gauche */}
              <View style={{ width: 330 }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Text style={Styles.informationPrincipale}>
                    {ligne?.produit?.nom || ""}
                  </Text>

                  {ligne?.produit?.conditionnement ? (
                    <Text
                      style={{
                        marginLeft: 6,
                        paddingHorizontal: 5,
                        paddingVertical: 2,
                        border: "1px solid black",
                      }}
                    >
                      {ligne.produit.conditionnement}
                    </Text>
                  ) : null}
                </View>

                {ligne?.produit?.description ? (
                  <Text style={Styles.informationSecondaire}>
                    {ligne.produit.description}
                  </Text>
                ) : null}

                {ligne?.commentaire ? <Text>{ligne.commentaire}</Text> : null}
              </View>

              {/* 4 colonnes moments */}
              {moments.map((m) => (
                <View
                  key={m.key}
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#EEEEEE",
                    borderRadius: 4,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text style={Styles.informationPrincipale}>
                    {ligne?.posologie?.[m.key] ?? ""}
                  </Text>
                  <Image
                    src={m.img}
                    style={{ width: 16, height: 16, marginTop: 4 }}
                  />
                </View>
              ))}
            </View>
          ))}

          {/* ===== CONSEIL PRISE (avant repas) ===== */}
          {afficherConseilPrise && data?.conseil_prise ? (
            <View
              style={{
                backgroundColor: "#EEEEEE",
                borderRadius: 4,
                marginTop: 20,
                paddingVertical: 8,
                paddingHorizontal: 10,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Text style={[Styles.informationPrincipale, { flex: 1, paddingRight: 10 }]}>
                Minimum {data?.conseil_prise?.duree} avant les repas avec de l&apos;eau :
              </Text>

              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {[
                  { key: "FRAICHE", label: "Fraiche" },
                  { key: "TIEDE", label: "Tiède" },
                  { key: "CHAUDE", label: "Chaude" },
                  { key: "TEMPEREE", label: "Tempérée" },
                ].map((t) => (
                  <View
                    key={t.key}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginLeft: 10,
                    }}
                  >
                    <Image
                      src={
                        data?.conseil_prise?.temperature === t.key
                          ? ImageCheckboxChecked
                          : ImageCheckbox
                      }
                      style={{ width: 14, height: 14, marginRight: 4 }}
                    />
                    <Text style={Styles.informationSecondaire}>{t.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          {/* ===== CONSEILS / TEXTE LIBRE ===== */}
          {data?.conseils_seance ? (
            <View style={{ marginTop: 14 }}>
              <Text style={Styles.informationPrincipale}>Conseils :</Text>
              <Text style={Styles.informationSecondaire}>{data.conseils_seance}</Text>
            </View>
          ) : null}

          {/* Bloc légal / avertissement */}
          <View
            style={{
              border: "1px solid black",
              padding: "10px 20px",
              marginTop: 14,
            }}
          >
            <Text style={[Styles.informationPrincipale, { textAlign: "center" }]}>
              LES SEANCES D'ENERGETIQUE CHINOISE NE REMPLACENT NI LA VISITE CHEZ
              VOTRE MEDECIN NI LES MEDICAMENTS QU'IL VOUS PRESCRIT
            </Text>
          </View>

          {/* ===== FOOTER PAGE X/Y ===== */}
          <View
            fixed
            style={{
              position: "absolute",
              left: 20,
              right: 20,
              bottom: 18,
              borderTop: "1px solid #EEE",
              paddingTop: 6,
              fontSize: 8,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text>
              {data?.cabinet?.informations_juridiques?.nom_entite_juridique || ""}
            </Text>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </Page>
      </Document>
    </DocumentPdf>
  );
}
